/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * Drop-in `react-i18next` replacement for English-only (`I18N_MODE=static`) builds.
 *
 * A Vite `resolve.alias` maps `react-i18next` → this module, so all ~300 existing
 * `useTranslation()` / `<Trans>` call sites keep working unchanged while i18next
 * core, the async locale backend, and react-i18next itself drop out of the client
 * bundle. Rendering resolves synchronously against the statically-bundled `en-US`
 * dictionary (see `./dictionary`).
 *
 * To restore full internationalization, build with `I18N_MODE=dynamic` (default):
 * the alias is not applied and the real `react-i18next` is used again — no source
 * changes to call sites.
 */
/* eslint-disable react/only-export-components -- this is a drop-in replacement for the
   `react-i18next` package, which by design exports hooks, an instance, and plugins
   alongside components; Fast Refresh does not apply to a vendored module shim. */
import {
    cloneElement,
    createElement,
    Fragment,
    isValidElement,
    type ComponentType,
    type ReactElement,
    type ReactNode,
} from 'react';
import {
    exists,
    getBundle,
    interpolate,
    rawString,
    STATIC_LANGUAGE,
    translate,
    type TranslateOptions,
} from './dictionary';

/**
 * `t` accepting a key or key-array (i18next tries each; we take the first). The
 * bound namespace is stashed on the function as `.ns` — mirrors how react-i18next
 * carries the namespace on `t` so `<Trans t={t}>` can resolve keys without an
 * explicit `ns` prop (real call sites pass `t` but no `ns`).
 */
type TFunc = ((key: string | string[], options?: TranslateOptions) => unknown) & { ns?: string };

/** `t` accepting a key or key-array (i18next tries each; we take the first). */
function makeT(namespace?: string): TFunc {
    const tf: TFunc = (key, options) => {
        const first = Array.isArray(key) ? key[0] : key;
        return translate(namespace, first, options);
    };
    tf.ns = namespace;
    return tf;
}

/**
 * Minimal stand-in for the i18next instance. Implements only the surface this
 * template touches (language/dir, resource-presence checks, namespace loading,
 * fixed-`t`, no-op lifecycle) so `root.tsx` and `i18n-client-init.ts` work as-is.
 */
export const staticI18nInstance = {
    language: STATIC_LANGUAGE,
    languages: [STATIC_LANGUAGE],
    isInitialized: true,
    options: {},
    t: makeT(),
    dir: (): 'ltr' | 'rtl' => 'ltr',
    hasResourceBundle: (): boolean => true,
    getResourceBundle: (_lng?: string, ns?: string): Record<string, unknown> => getBundle(ns),
    exists: (key: string): boolean => exists(undefined, key),
    getFixedT: (_lng: string | null, ns?: string): TFunc => makeT(ns),
    loadNamespaces: (_ns?: string | string[], callback?: () => void): Promise<void> => {
        callback?.();
        return Promise.resolve();
    },
    changeLanguage: (): Promise<TFunc> => Promise.resolve(makeT()),
    on: (): void => {
        /* no event system in static mode */
    },
    off: (): void => {
        /* no event system in static mode */
    },
    use() {
        return this;
    },
    init(): Promise<TFunc> {
        return Promise.resolve(makeT());
    },
};

export type StaticI18n = typeof staticI18nInstance;

export function useTranslation(
    ns?: string | string[],
    _options?: unknown
): { t: TFunc; i18n: StaticI18n; ready: true } {
    const namespace = Array.isArray(ns) ? ns[0] : ns;
    return { t: makeT(namespace), i18n: staticI18nInstance, ready: true };
}

/** Provider is a no-op passthrough — translations come from the static dictionary. */
export function I18nextProvider({ children }: { children?: ReactNode; i18n?: unknown }): ReactNode {
    return createElement(Fragment, null, children);
}

/** Registered as an i18next plugin by callers; inert here. */
export const initReactI18next = {
    type: '3rdParty' as const,
    init(): void {
        /* inert: no react-i18next binding to initialize */
    },
};

export function withTranslation() {
    return function <P extends object>(Component: ComponentType<P>) {
        return function WithTranslation(props: P): ReactNode {
            return createElement(Component, { ...props, t: makeT(), i18n: staticI18nInstance } as P);
        };
    };
}

// --- <Trans> support -------------------------------------------------------

type TransComponents = readonly ReactElement[] | Record<string, ReactElement>;

interface TransProps {
    i18nKey?: string;
    ns?: string;
    t?: TFunc;
    count?: number;
    values?: Record<string, unknown>;
    components?: TransComponents;
    children?: ReactNode;
}

/**
 * Parse a translated string containing `<0>...</0>` (indexed) or `<bold>...</bold>`
 * (named) tags and substitute the matching element from `components`, using the
 * inner text as that element's children. Interpolation of `{{var}}` runs first.
 */
function renderTransString(text: string, components?: TransComponents): ReactNode[] {
    if (!components) return [text];
    const nodes: ReactNode[] = [];
    const tagRe = /<([a-zA-Z0-9]+)>([\s\S]*?)<\/\1>/g;
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    let key = 0;

    const pick = (name: string): ReactElement | undefined => {
        if (Array.isArray(components)) {
            const idx = Number(name);
            return Number.isNaN(idx) ? undefined : components[idx];
        }
        return (components as Record<string, ReactElement>)[name];
    };

    while ((match = tagRe.exec(text)) !== null) {
        if (match.index > lastIndex) nodes.push(text.slice(lastIndex, match.index));
        const [, name, inner] = match;
        const element = pick(name);
        if (element && isValidElement(element)) {
            nodes.push(cloneElement(element, { key: `trans-${key++}` }, inner));
        } else {
            nodes.push(inner);
        }
        lastIndex = tagRe.lastIndex;
    }
    if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
    return nodes;
}

export function Trans({ i18nKey, ns, t, count, values, components }: TransProps): ReactNode {
    if (!i18nKey) return null;
    // Namespace resolution matches react-i18next: an explicit `ns` prop wins,
    // otherwise fall back to the namespace bound on the `t` prop. Real call sites
    // (cart-empty, BNPL) pass `t={t}` with no `ns`, so without this the bare key
    // resolves at the dictionary root, misses, and the literal key renders.
    const namespace = ns ?? t?.ns;
    const options: TranslateOptions = { ...(values ?? {}) };
    if (typeof count === 'number') options.count = count;
    // Take the raw (still-tagged) string for the resolved plural form, interpolate
    // its {{vars}}, then substitute <tag> markers with the provided components.
    const withValues = interpolate(rawString(namespace, i18nKey, options), options);
    return createElement(Fragment, null, ...renderTransString(withValues, components));
}
